/** Контракты оформления заказа — по `/api/system/`, `/api/paytypes/List`, `/api/delivery/Create`. */

export interface ApiPayType {
  id: number
  name: string
  imageUrl: string | null
  tgImage: string | null
  isDeleted: boolean
}

/** Настройки бренда. Полей много, нам отсюда нужны только лимиты заказа. */
export interface ApiSystemSettings {
  /** Порог, с которого запрещена оплата наличными */
  OrderMaxSum?: number
  /** На сколько дней вперёд можно оформить предзаказ; у Oqtepa не приходит */
  PreorderDays?: number
}

export interface OrderItemPayload {
  productId: string
  quantity: number
  price: number
  modifiers: { modifierId: string; count: number }[]
  /** Убранные ингредиенты состава — просто массив id, без количеств */
  excludedIngredients?: string[]
}

export interface CreateOrderPayload {
  /** Захардкожен русский, как в веб-версии: язык бэкенд берёт из заголовка */
  language: number
  contactName: string
  phone1: string
  phone2: string | null
  paymentPhone: string
  /**
   * Запись из справочника адресов клиента. Строку адреса бэкенд всё равно
   * ждёт рядом: у самовывоза и у точки, выбранной на карте без сохранения,
   * идентификатора нет — там уходит null.
   */
  addressId: number | null
  address: string
  /**
   * Детали адреса отдельными полями — их же показывает приложение курьера.
   * Раньше они склеивались в `addressComment` одной строкой, и разобрать её
   * обратно было нечем. `house` клиент не вводит: он приходит из разбора
   * адреса Яндексом и уходит молча, когда геокодер его знает.
   */
  house: string
  entrance: string
  floor: string
  flat: string
  /** Только то, что клиент написал сам: «код домофона», «позвонить заранее» */
  addressComment: string
  longitude: string
  latitude: string
  /** 0 — наличные, 3 — онлайн */
  paymentTypeId: number
  plannedDateType: number
  /** `YYYY-MM-DDTHH:mm:00`; null, когда предзаказ выключен */
  plannedDateTime: string | null
  order: {
    notes: string
    restaurantId: string
    regionId: number
    /** 0 — доставка, 200 — самовывоз */
    orderTypeId: number
    customerId: string
    orderItems: OrderItemPayload[]
    /** Сколько наборов приборов положить — счётчик «Приборы» в корзине */
    person: number
    /** Сколько списано бонусами */
    payByCard: number
    /** Блок появляется, только когда промокод принят сервисом */
    promo?: {
      promoId: number
      promoCode: string
      discount: number
      /** Числовой код типа промо — `PROMO_VIEW` */
      view: number
    }
  }
}

/** Бэкенд отвечает 200 и с ошибкой в теле — поля опциональные. */
export interface CreateOrderResponse {
  orderId?: string
  error?: unknown
}

/** Коды статусов заказа — §11 LOGIC.md. */
export const ORDER_STATUS = {
  new: 0,
  payed: 1,
  accepted: 2,
  /** В LOGIC.md его нет, но бэкенд отдаёт: «Поиск курьера» */
  searchDriver: 3,
  cooking: 10,
  ready: 11,
  delivery: 200,
  delivered: 201,
  forming: 250,
  canceled: 255,
} as const

export interface ApiOrder {
  orderId: string
  /** `/delivery/List` отдаёт строкой, `/delivery/Get` — числом. Приводим сами. */
  number: string | number
  dateTime: string
  regionName: string
  restaurantName: string
  notes: string
  countOfItems: number
  /** Сумма позиций со скидками — из неё складывается итог заказа */
  sumOfItems: number
  /** Та же сумма, но до скидок */
  sumOfItemsWithoutDiscount?: number
  /** Скидка на весь заказ: сумма `discounts` */
  discountSum?: number
  discounts?: ApiDiscount[] | null
  /** Приходит нулём даже у оплаченных заказов — сумму считаем сами */
  paymentSum: number
  orderTypeId: number
  orderTypeName: string
  seqNumber: string | null
}

/** Строка списка `/delivery/Get` отдаёт то же плюс состав заказа. */
export interface ApiOrderListItem {
  plannedDateTime: string
  contactName: string
  phone1: string
  /** У самовывоза адреса нет — вместо него имя ресторана в `order` */
  address: string | null
  paymentTypeName: string
  priceOfDelivery: number
  driverName: string | null
  driverPhone: string | null
  statusName: string
  statusCode: number
  ratingProduct: number
  ratingService: number
  ratingDelivery: number
  order: ApiOrder
}

/** Позиция в оформленном заказе — состав приходит только в `/delivery/Get`. */
/**
 * Из чего сложилась скидка. Название приходит готовым и на языке клиента —
 * показываем его как есть, а не собираем своё из `source`.
 */
export interface ApiDiscount {
  source: 'PromoCode' | 'Loyalty'
  name: string
  sum: number
}

export interface ApiOrderItem {
  orderItemId: string
  productId: string
  productName: string
  /** Цена за штуку с учётом скидок на саму позицию: у подарка это ноль */
  price: number
  /** Цена за штуку по меню, до всяких скидок */
  orgPrice?: number
  quantity: number
  imageUrl: string | null
  /** Позицию отдали бесплатно по акции */
  isFree?: boolean
  /** `orgPrice` × количество */
  sumWithoutDiscount?: number
  /** Скидка на позицию за всё количество, включая её долю скидки на заказ */
  discountSum?: number
  /** Итог по позиции — его и платит клиент */
  sumWithDiscount?: number
  discounts?: ApiDiscount[] | null
  /** Проверено на живых заказах: id есть, количество — на одну штуку позиции */
  modifiers: { modifierId: string; name: string | null; count: number; price: number }[] | null
}

/**
 * Детали заказа. Тот же корень, что у строки списка, плюс состав, координаты
 * точки и причина отмены.
 */
export interface ApiOrderDetails extends Omit<ApiOrderListItem, 'order'> {
  orderDateTime: string
  cancelReason: string | null
  /**
   * Ссылка на оплату в Rahmat («https://app.rhmt.uz/u/{rahmatId}/{guid}»).
   * Появляется у заказов с оплатой Rahmat, у остальных — null.
   */
  rahmatReceiptUrl: string | null
  entrance: string | null
  floor: string | null
  flat: string | null
  order: ApiOrder & {
    orderItems: ApiOrderItem[]
    restaurantId: string
  }
}

/** Оценка заказа: три отдельные шкалы, каждая 1..5. */
export interface RatingPayload {
  orderId: string
  ratingProduct: number
  ratingService: number
  ratingDelivery: number
}
